import type { EntityFormErrorMap } from '@klear/ui/entity-form-state'
import { Money } from '@klear/money'
import React, { useEffect, useMemo, useRef } from 'react'
import type { Draft } from 'immer'
import { Building2, FileStack, Globe, LineChart, MapPin, RefreshCw, type LucideIcon } from 'lucide-react'
import {
  FITCH_RATING_OPTIONS,
  JCR_RATING_OPTIONS,
  MOODYS_RATING_OPTIONS,
  SP_RATING_OPTIONS,
  ALLIANZ_RATING_OPTIONS,
  CREDIT_CLASSIFICATION_OPTIONS
} from '@klear/invoice-offer-fee-rate/options'
import { CREDIT_CLASSIFICATION_IG, CREDIT_CLASSIFICATION_INSURED, ORGANIZATION_SIZES } from '@klear/constants'
import { COUNTRY_CODE_OPTIONS, COUNTRY_CODE_US } from '@klear/constants/countries'
import { DEFAULT_CURRENCY_CODE, CURRENCY_CODE_OPTIONS, type CurrencyCode } from '@klear/constants/currency'
import { moneyFromMinorDigits } from '@klear/ui/components/money-form-value'
import { moneyFromFormValue } from '@klear/ui/components/money-form-value'
import { Button } from '@klear/ui/components/button'
import { Card, CardContent, CardHeader, CardTitle } from '@klear/ui/components/card'
import { Combobox, type ComboboxOption } from '@klear/ui/components/combobox'
import { Input } from '@klear/ui/components/input'
import { Label } from '@klear/ui/components/label'
import { useConfirmDialog } from '@klear/ui/components/alert-dialog'
import { cn } from '@klear/ui/lib/utils'

import type { CustomerWizardFormValues } from '@klear/customer-ui/customer-commit-form'
import type { CustomerFormFieldPath, CustomerFormFieldValue } from '@klear/customer-ui/customer-form-state'
import { creditReadToForm } from '@klear/credit/wire.client'
import { useCreditMutations } from '@klear/customer-ui/use-credit'
import {
  applyCreditClassificationChange,
  clearCustomerCreditFields,
  CUSTOMER_CREDIT_FIELD_PATHS
} from '@klear/customer-ui/customer-wizard-rules'

export type CustomerFormUpdate = (
  recipe: (
    draft: Draft<CustomerWizardFormValues>
  ) => void | CustomerFormFieldPath | readonly CustomerFormFieldPath[] | undefined,
  dirtyPaths?: CustomerFormFieldPath | readonly CustomerFormFieldPath[]
) => void

export interface CustomerFormControlProps {
  values: CustomerWizardFormValues
  errors: EntityFormErrorMap
  onFieldChange: (path: CustomerFormFieldPath, value: CustomerFormFieldValue) => void
  onFormUpdate: CustomerFormUpdate
}

interface CustomerFieldGroupProps extends CustomerFormControlProps {
  isEditing?: boolean
}

function errorMessage(errors: EntityFormErrorMap, path: CustomerFormFieldPath): string | undefined {
  return errors[path]
}

function FieldShell({
  label,
  htmlFor,
  icon: Icon,
  helperText,
  error,
  className,
  children
}: {
  label: string
  htmlFor?: string
  icon?: LucideIcon
  helperText?: string
  error?: string
  className?: string
  children: React.ReactNode
}) {
  return (
    <div className={cn('space-y-2', className)}>
      <Label htmlFor={htmlFor} className="flex items-center gap-2">
        {Icon ? <Icon className="h-4 w-4" /> : null}
        {label}
      </Label>
      {children}
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
      {helperText ? <p className="text-sm text-muted-foreground">{helperText}</p> : null}
    </div>
  )
}

function TextField({
  path,
  label,
  value,
  errors,
  onFieldChange,
  disabled,
  icon,
  placeholder,
  helperText,
  className
}: {
  path: CustomerFormFieldPath
  label: string
  value: string | number | undefined
  errors: EntityFormErrorMap
  onFieldChange: CustomerFormControlProps['onFieldChange']
  disabled?: boolean
  icon?: LucideIcon
  placeholder?: string
  helperText?: string
  className?: string
}) {
  const message = errorMessage(errors, path)
  return (
    <FieldShell label={label} htmlFor={path} icon={icon} helperText={helperText} error={message} className={className}>
      <Input
        id={path}
        disabled={disabled}
        value={value ?? ''}
        placeholder={placeholder}
        className={message ? 'border-destructive' : ''}
        onChange={(event) => onFieldChange(path, event.target.value)}
      />
    </FieldShell>
  )
}

function SelectField<T extends string | number>({
  path,
  label,
  value,
  options,
  errors,
  onChange,
  disabled,
  placeholder,
  helperText,
  className,
  valueType = 'string'
}: {
  path: CustomerFormFieldPath
  label: string
  value: T | ''
  options: ComboboxOption<T>[]
  errors: EntityFormErrorMap
  onChange: (value: T | '') => void
  disabled?: boolean
  placeholder?: string
  helperText?: string
  className?: string
  valueType?: 'string' | 'number'
}) {
  const message = errorMessage(errors, path)
  return (
    <FieldShell label={label} htmlFor={path} helperText={helperText} error={message} className={className}>
      <Combobox<T>
        value={value}
        disabled={disabled}
        options={options}
        placeholder={placeholder}
        onChange={onChange}
        error={message ? { message } : undefined}
        valueType={valueType}
        className="w-full"
      />
    </FieldShell>
  )
}

function hasMeaningfulMoney(value: CustomerWizardFormValues['allianzCreditLimit']): boolean {
  const money = moneyFromFormValue(value)
  return money != null && money.amount !== 0n
}

function MoneyField({
  value,
  errors,
  onFieldChange,
  disabled
}: {
  value: CustomerWizardFormValues['allianzCreditLimit']
  errors: EntityFormErrorMap
  onFieldChange: CustomerFormControlProps['onFieldChange']
  disabled?: boolean
}) {
  const path = 'allianzCreditLimit'
  const money = moneyFromFormValue(value)
  const message = errorMessage(errors, path)
  const currency = money?.currency ?? DEFAULT_CURRENCY_CODE

  return (
    <FieldShell label="Allianz credit limit" htmlFor={path} error={message}>
      <div className="flex">
        <Input
          id={path}
          type="text"
          className={cn('rounded-r-none border-r-0', message && 'border-destructive')}
          placeholder="0.00"
          disabled={disabled}
          value={money?.toString() ?? ''}
          onChange={(event) => {
            const digits = event.target.value.replace(/[^\d]/g, '')
            if (digits === '') {
              onFieldChange(path, undefined)
              return
            }
            onFieldChange(path, moneyFromMinorDigits(digits, currency))
          }}
        />
        <Combobox<CurrencyCode>
          className="w-22 shrink-0 rounded-l-none border-l-0"
          value={currency}
          options={CURRENCY_CODE_OPTIONS}
          placeholder="CCY"
          disabled={disabled}
          onChange={(next) => {
            const nextCurrency = (next === '' ? DEFAULT_CURRENCY_CODE : next) as CurrencyCode
            const amount = money?.amount ?? 0n
            onFieldChange(path, Money.fromMinorUnits(amount, nextCurrency))
          }}
          error={message ? { message } : undefined}
        />
      </div>
    </FieldShell>
  )
}

export function CustomerOrganizationFields({
  values,
  errors,
  onFieldChange,
  isEditing = true,
  showParentOrganization = false
}: CustomerFieldGroupProps & { showParentOrganization?: boolean }) {
  const customerSizes = useMemo(
    () =>
      ORGANIZATION_SIZES.map((size) => ({
        value: size,
        label: size
      })),
    []
  )

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-5 w-5" />
          Organization
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <TextField
          label="Customer Name"
          path="name"
          value={values.name}
          errors={errors}
          onFieldChange={onFieldChange}
          disabled={!isEditing}
        />
        <TextField
          label="Customer Description"
          path="description"
          value={values.description}
          errors={errors}
          onFieldChange={onFieldChange}
          disabled={!isEditing}
        />
        {showParentOrganization ? (
          <TextField
            label="Parent Organization Name"
            path="parentOrganization"
            value={values.parentOrganization}
            errors={errors}
            onFieldChange={onFieldChange}
            disabled={!isEditing}
            helperText="Optional"
          />
        ) : null}
        <SelectField
          label="Size"
          path="size"
          value={values.size ?? ''}
          options={customerSizes}
          errors={errors}
          disabled={!isEditing}
          onChange={(value) => onFieldChange('size', value === '' ? undefined : value)}
        />
      </CardContent>
    </Card>
  )
}

export function CustomerLocationFields({ values, errors, onFieldChange, isEditing = true }: CustomerFieldGroupProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MapPin className="h-5 w-5" />
          Address & website
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <SelectField
          label="Country"
          path="address.country"
          value={values.address.country}
          options={COUNTRY_CODE_OPTIONS}
          errors={errors}
          disabled={!isEditing}
          placeholder="Select a country"
          onChange={(value) => onFieldChange('address.country', value === '' ? COUNTRY_CODE_US : value)}
        />
        <TextField
          label="Address line 1"
          path="address.line1"
          value={values.address.line1}
          errors={errors}
          onFieldChange={onFieldChange}
          disabled={!isEditing}
          helperText="Optional for some regions"
        />
        <div className="grid grid-cols-2 gap-4">
          <TextField
            label="City"
            path="address.city"
            value={values.address.city}
            errors={errors}
            onFieldChange={onFieldChange}
            disabled={!isEditing}
          />
          <TextField
            label="State / region"
            path="address.state"
            value={values.address.state}
            errors={errors}
            onFieldChange={onFieldChange}
            disabled={!isEditing}
          />
        </div>
        <TextField
          label="Postal code"
          path="address.postalCode"
          value={values.address.postalCode}
          errors={errors}
          onFieldChange={onFieldChange}
          disabled={!isEditing}
        />
        <TextField
          label="Website"
          path="website"
          value={values.website}
          icon={Globe}
          placeholder="https://example.com"
          errors={errors}
          onFieldChange={onFieldChange}
          disabled={!isEditing}
        />
      </CardContent>
    </Card>
  )
}

export function CustomerIdentifierFields({ values, errors, onFieldChange, isEditing = true }: CustomerFieldGroupProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileStack className="h-5 w-5" />
          Identifiers
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <TextField
            label="DUNS Number"
            path="dunsNumber"
            value={values.dunsNumber}
            errors={errors}
            onFieldChange={onFieldChange}
            disabled={!isEditing}
            helperText="9 digits"
          />
          <TextField
            label="LEI"
            path="lei"
            value={values.lei}
            errors={errors}
            onFieldChange={onFieldChange}
            disabled={!isEditing}
            helperText="ISO 17442 (20 characters)"
          />
          <TextField
            label="EIN / FEIN"
            path="ein"
            value={values.ein}
            errors={errors}
            onFieldChange={onFieldChange}
            disabled={!isEditing}
            helperText="9 digits (NN-NNNNNNN)"
          />
          <TextField
            label="VAT Number"
            path="vatNumber"
            value={values.vatNumber}
            errors={errors}
            onFieldChange={onFieldChange}
            disabled={!isEditing}
            helperText="Add country prefix if applicable"
          />
        </div>
      </CardContent>
    </Card>
  )
}

const IG_RATING_FIELDS: Array<{
  label: string
  path: 'spRating' | 'moodysRating' | 'fitchRating' | 'jcrRating'
  options: ComboboxOption<string>[]
}> = [
  { label: 'S&P rating', path: 'spRating', options: SP_RATING_OPTIONS },
  { label: "Moody's rating", path: 'moodysRating', options: MOODYS_RATING_OPTIONS },
  { label: 'Fitch rating', path: 'fitchRating', options: FITCH_RATING_OPTIONS },
  { label: 'JCR rating', path: 'jcrRating', options: JCR_RATING_OPTIONS }
]

export function CustomerCreditFields({
  values,
  errors,
  onFieldChange,
  onFormUpdate,
  isEditing = true,
  creditEditable = true,
  organizationId
}: CustomerFieldGroupProps & {
  creditEditable?: boolean
  organizationId?: number
}) {
  const { confirm, ConfirmDialog } = useConfirmDialog()
  const { refreshAllianzData, isRefreshingAllianz } = useCreditMutations()
  const prevClassification = useRef<{
    initialized: boolean
    value: CustomerWizardFormValues['creditClassification']
  }>({
    initialized: false,
    value: values.creditClassification
  })

  const fieldEditing = isEditing && creditEditable
  const creditClassification = values.creditClassification
  const hasRatingsToClear = !!(
    values.creditClassification ||
    values.moodysRating ||
    values.spRating ||
    values.fitchRating ||
    values.jcrRating ||
    values.allianzRating ||
    hasMeaningfulMoney(values.allianzCreditLimit)
  )

  useEffect(() => {
    const prev = prevClassification.current
    if (!prev.initialized) {
      prev.initialized = true
      prev.value = creditClassification
      return
    }
    if (prev.value === creditClassification) {
      return
    }
    prev.value = creditClassification

    onFormUpdate(
      (draft) => {
        applyCreditClassificationChange(draft, creditClassification)
      },
      ['moodysRating', 'spRating', 'fitchRating', 'jcrRating', 'allianzRating', 'allianzCreditLimit']
    )
  }, [creditClassification, onFormUpdate])

  const canRefreshAllianz =
    fieldEditing &&
    values.creditClassification === CREDIT_CLASSIFICATION_INSURED &&
    organizationId != null &&
    organizationId > 0
  const hasAllianzEhid = Boolean(values.allianzEhid?.trim())

  const handleClearClick = () => {
    confirm({
      title: 'Clear Credit Classification & Ratings',
      description: 'Clears these fields in the form. Save on the Review step to persist changes.',
      onConfirm: () => {
        onFormUpdate(
          (draft) => {
            clearCustomerCreditFields(draft)
          },
          [...CUSTOMER_CREDIT_FIELD_PATHS]
        )
      }
    })
  }

  const handleRefreshAllianz = () => {
    if (organizationId == null || organizationId < 1 || !hasAllianzEhid) return
    confirm({
      title: 'Refresh Allianz credit',
      description: 'Pulls live Allianz grade and credit limit and saves them immediately.',
      onConfirm: async () => {
        const refreshed = await refreshAllianzData({ organizationId })
        if (refreshed == null) return
        const next = creditReadToForm(refreshed)
        onFormUpdate((draft) => {
          draft.allianzRating = next.allianzRating
          draft.allianzCreditLimit = next.allianzCreditLimit
          draft.creditClassification = next.creditClassification
        })
      }
    })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <LineChart className="h-5 w-5" />
          Credit classification & ratings
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Classification drives which ratings apply.</span>
          <div className="flex items-center gap-2">
            {canRefreshAllianz ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleRefreshAllianz}
                disabled={isRefreshingAllianz || !hasAllianzEhid}
                title={hasAllianzEhid ? undefined : 'Link an Allianz EHID first'}
              >
                <RefreshCw className={cn('mr-1.5 h-3.5 w-3.5', isRefreshingAllianz && 'animate-spin')} aria-hidden />
                Refresh Allianz
              </Button>
            ) : null}
            {fieldEditing && hasRatingsToClear ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleClearClick}
                className="text-red-600 hover:bg-red-50 hover:text-red-700"
              >
                Clear
              </Button>
            ) : null}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <SelectField
            label="Credit classification"
            path="creditClassification"
            value={values.creditClassification ?? ''}
            options={CREDIT_CLASSIFICATION_OPTIONS}
            errors={errors}
            disabled={!fieldEditing}
            placeholder="Select classification"
            helperText="IG uses S&P / Moody's / Fitch / JCR; Insured uses Allianz"
            onChange={(value) => onFieldChange('creditClassification', value === '' ? undefined : value)}
          />
        </div>
        {values.creditClassification === CREDIT_CLASSIFICATION_IG && (
          <div className="grid grid-cols-2 gap-4">
            {IG_RATING_FIELDS.map(({ label, path, options }) => (
              <SelectField
                key={path}
                label={label}
                path={path}
                value={values[path] ?? ''}
                options={options}
                errors={errors}
                disabled={!fieldEditing}
                placeholder="Select rating"
                onChange={(value) => onFieldChange(path, value === '' ? undefined : value)}
              />
            ))}
          </div>
        )}
        {values.creditClassification === CREDIT_CLASSIFICATION_INSURED && (
          <>
            <div className="grid grid-cols-3 gap-4">
              <SelectField
                label="Allianz rating"
                path="allianzRating"
                value={values.allianzRating ?? ''}
                options={ALLIANZ_RATING_OPTIONS}
                errors={errors}
                disabled={!fieldEditing}
                placeholder="Select rating"
                onChange={(value) => onFieldChange('allianzRating', value === '' ? undefined : value)}
              />
            </div>
            <MoneyField
              value={values.allianzCreditLimit}
              errors={errors}
              onFieldChange={onFieldChange}
              disabled={!fieldEditing}
            />
          </>
        )}
      </CardContent>
      {ConfirmDialog}
    </Card>
  )
}
